import * as React from "react";

import { SelectContent } from "@/components/ui/select";

import { ProjectStatusFilter } from "./ProjectStatusFilter";
import { statusFilterOptionValues, type ProjectStatusFilterValue } from "./projectStatus";
import type { ProjectStatusFilterState } from "./useProjectStatusFilter";

type ProjectSelectContentProps = React.ComponentPropsWithoutRef<typeof SelectContent> & {
  /** From {@link useProjectStatusFilter}. Below the split threshold the
   *  header is omitted and this is a plain SelectContent. */
  statusFilter: Pick<
    ProjectStatusFilterState<unknown>,
    "filter" | "setFilter" | "counts" | "showFilter"
  >;
};

/**
 * The dropdown of a Select-based project choice, with the status filter as a
 * header pinned above the projects. Clicking it is safe — Radix only closes a
 * Select on a pointer-up OUTSIDE its content. Tab is trapped inside the
 * content, so ←/→ (which Radix leaves alone) switch the filter from the
 * keyboard.
 */
export const ProjectSelectContent = React.forwardRef<
  React.ElementRef<typeof SelectContent>,
  ProjectSelectContentProps
>(({ statusFilter, children, onKeyDown, ...props }, ref) => {
  const { filter, setFilter, counts, showFilter } = statusFilter;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(event);
    if (!showFilter || event.defaultPrevented) return;
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    const next = stepFilter(filter, counts, event.key === "ArrowRight" ? 1 : -1);
    if (next) setFilter(next);
    event.preventDefault();
  };

  return (
    <SelectContent ref={ref} onKeyDown={handleKeyDown} {...props}>
      {showFilter && (
        <div className="sticky top-0 z-10 -mx-1 -mt-1 mb-1 border-b bg-popover px-2 py-1.5">
          <ProjectStatusFilter
            variant="inline"
            value={filter}
            onValueChange={setFilter}
            counts={counts}
          />
        </div>
      )}
      {children}
    </SelectContent>
  );
});
ProjectSelectContent.displayName = "ProjectSelectContent";

/** The next pickable option in `direction`, skipping empty ones; null at either end. */
function stepFilter(
  current: ProjectStatusFilterValue,
  counts: Record<ProjectStatusFilterValue, number>,
  direction: 1 | -1
): ProjectStatusFilterValue | null {
  const order = statusFilterOptionValues(true);
  for (let i = order.indexOf(current) + direction; i >= 0 && i < order.length; i += direction) {
    if (counts[order[i]] > 0) return order[i];
  }
  return null;
}
